import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Shield } from "lucide-react";

export default function Settings() {
    const { user, refresh } = useAuth();
    const [currentPassword, setCurrentPassword] = useState("");
    const [newEmail, setNewEmail] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!currentPassword) {
            toast.error("Enter your current password to confirm changes");
            return;
        }
        if (newPassword && newPassword !== confirmPassword) {
            toast.error("Passwords do not match");
            return;
        }
        if (!newEmail && !newPassword) {
            toast.error("Nothing to change");
            return;
        }
        setSaving(true);
        try {
            await api.post("/auth/change-credentials", {
                current_password: currentPassword,
                new_email: newEmail || null,
                new_password: newPassword || null,
            });
            toast.success("Credentials updated");
            setCurrentPassword("");
            setNewEmail("");
            setNewPassword("");
            setConfirmPassword("");
            await refresh();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const inputCls = "bg-zinc-900 border-white/10 text-zinc-100 placeholder:text-zinc-500 focus-visible:ring-brand";
    const labelCls = "text-xs font-bold uppercase tracking-[0.15em] text-zinc-500";

    return (
        <div className="p-6 md:p-10 lg:p-12 max-w-3xl mx-auto">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand mb-2">Settings</div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-zinc-100">Admin credentials</h1>
            <p className="mt-2 text-zinc-400 text-sm">
                Currently signed in as <span className="font-medium text-zinc-200">{user?.email}</span>.
            </p>

            <form onSubmit={handleSubmit} className="mt-10 bg-zinc-900/70 border border-white/10 rounded-xl p-6 md:p-8 space-y-6">
                <div className="flex items-center gap-3 pb-4 border-b border-white/10">
                    <div className="h-9 w-9 rounded-lg bg-brand/15 text-brand flex items-center justify-center">
                        <Shield className="h-4 w-4" />
                    </div>
                    <div>
                        <div className="font-display font-semibold text-zinc-100">Change email or password</div>
                        <div className="text-xs text-zinc-400">Confirm with your current password.</div>
                    </div>
                </div>

                <div className="space-y-2">
                    <Label className={labelCls}>Current password</Label>
                    <Input
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        required
                        autoComplete="current-password"
                        data-testid="settings-current-password"
                        className={inputCls}
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <Label className={labelCls}>New email (optional)</Label>
                        <Input
                            type="email"
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                            placeholder={user?.email}
                            data-testid="settings-new-email"
                            className={inputCls}
                        />
                    </div>
                    <div className="space-y-2">
                        <Label className={labelCls}>New password (optional)</Label>
                        <Input
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            autoComplete="new-password"
                            data-testid="settings-new-password"
                            className={inputCls}
                        />
                    </div>
                    <div className="space-y-2 md:col-start-2">
                        <Label className={labelCls}>Confirm new password</Label>
                        <Input
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            autoComplete="new-password"
                            data-testid="settings-confirm-password"
                            className={inputCls}
                        />
                    </div>
                </div>

                <div className="pt-2">
                    <Button
                        type="submit"
                        disabled={saving}
                        data-testid="settings-save-button"
                        className="bg-brand hover:bg-brand-hover text-white"
                    >
                        {saving ? "Saving…" : "Save changes"}
                    </Button>
                </div>
            </form>
        </div>
    );
}
